import React, { useState, useEffect } from "react";
import { useAuth } from "../../services/AuthContext";
import { addFunds, fetchChildren } from "../../services/Api";
import PopupWrapper from "../../components/PopupWrapper";
import { useToast } from '../../services/ToastContext';
import { useRefresh } from "../../services/RefreshContext";

// New component for the slide-up popup
const AddFundsForm = ({ isOpen, onClose, child }) => {
  const { familyID } = useAuth();
  const { showToast } = useToast();
  const { refreshCounter, triggerRefresh } = useRefresh();

  const [children, setChildren] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [checkedOptions, setCheckedOptions] = useState({});
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [inputErrors, setInputErrors] = useState({
    inputAmount: "",
    inputDescription: "",
    inputCheckedOption: ""
  });

  // Fetch children when component mounts
  useEffect(() => {
    const getChildren = async () => {
      const childList = await fetchChildren(familyID);
      setChildren(childList);

      // Initialize checkbox state dynamically
      const initialCheckedState = childList.reduce((acc, child) => {
        acc[child.ChildID] = false;
        return acc;
      }, {});
      setCheckedOptions(initialCheckedState);
    };

    getChildren();
  }, [familyID, refreshCounter]);

  useEffect(() => {
    if (child && isOpen && children.length > 0) {
      const updatedCheckedOptions = children.reduce((acc, c) => {
        acc[c.ChildID] = c.ChildID === child.ChildID;
        return acc;
      }, {});
      setCheckedOptions(updatedCheckedOptions);
      setSelectAll(false);
    }
  }, [child, isOpen, children]);

  const validateAddFundsForm = () => {
    let valid = true;
    const newErrors = {
      inputAmount: "",
      inputDescription: "",
      inputCheckedOption: ""
    };

    if (!description.trim()) {
      newErrors.inputDescription = "Description is required";
      valid = false;
    }

    if (!amount || isNaN(amount)) {
      newErrors.inputAmount = "Enter a valid number";
      valid = false;
    }

    if (!Object.values(checkedOptions).some(value => value === true)) {
      newErrors.inputCheckedOption = "Must select a child";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  // Handle "All Kids" checkbox toggle
  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);

    setCheckedOptions((prev) => {
      const updatedState = { ...prev };
      children.forEach((child) => {
        updatedState[child.ChildID] = isChecked;
      });
      return updatedState;
    });
  };

  // Handle individual checkbox toggle
  const handleCheckboxChange = (event) => {
    const { id, checked } = event.target;
    setCheckedOptions((prev) => {
      const updatedState = { ...prev, [id]: checked };

      const allChecked = children.every((child) => updatedState[child.ChildID] === true);
      setSelectAll(allChecked);

      return updatedState;
    });
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? "" : `${Math.abs(num).toFixed(2)}`;
  };

  const handleAddFunds = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateAddFundsForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const formattedAmount = Math.abs(amount);
      await addFunds(formattedAmount, description, checkedOptions);

      setAmount("");
      setDescription("");
      setCheckedOptions({});
      setSelectAll(false);

      triggerRefresh();
      showToast("Funds Added");
      onClose();
    } catch (error) {
      setError(error.message || "An error occurred while adding funds.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <PopupWrapper
        isOpen={isOpen}
        onClose={onClose}
        title="How Much Money Do You Want to Add?"
        style={{ height: "90%" }}
      >
        <form className="user">
          {/* Amount to be Added */}
          <div className="form-group">
            <input
              type="number"
              className="form-control form-control-user"
              id="exampleInputAmount"
              placeholder="Enter Amount..."
              min="0"
              step="0.25"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={(e) => setAmount(formatCurrency(e.target.value))}
            />
            {inputErrors.inputAmount && (
              <p className="text-danger">{inputErrors.inputAmount}</p>
            )}
          </div>

          {/* Description */}
          <div className="form-group">
            <input
              type="text"
              className="form-control form-control-user"
              id="descriptionInputAddFunds"
              placeholder="Description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {inputErrors.inputDescription && (
              <p className="text-danger">{inputErrors.inputDescription}</p>
            )}
          </div>

          {/* Checkboxes Section */}
          <div className="form-group">
            {/* "Select All" Checkbox */}
            {children && children.length > 0 && (
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="selectAll"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <label
                  className="custom-control-label"
                  htmlFor="selectAll"
                >
                  {selectAll ? "Deselect All" : "Select All"}
                </label>
              </div>
            )}

            <hr />

            {/* Dynamically Rendered Individual Checkboxes */}
            <div className="checkbox-group">
              {children ? (children.map((child) => (
                <div
                  key={child.ChildID}
                  className="custom-control custom-checkbox checkbox-item"
                >
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id={child.ChildID}
                    checked={checkedOptions[child.ChildID] || false}
                    onChange={handleCheckboxChange}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor={child.ChildID}
                  >
                    {child.FirstName}
                  </label>
                </div>
              ))) : "Problem finding kids"}
            </div>
            {inputErrors.inputCheckedOption && (
              <p className="text-danger">{inputErrors.inputCheckedOption}</p>
            )}
          </div>


          {error && <p className="text-danger">{error}</p>}

          <div className="popupButtons">
            <button
              type="button"
              className="cancel-btn p-3 grey-color"
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className={`confirm-btn p-3${isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              disabled={isLoading}
              onClick={handleAddFunds}
            >
              {isLoading ? "Adding..." : "Add to Account"}
            </button>
          </div>

          {/* <hr />
          <div className="text-center">
            <h3>Or did you want to... </h3>
            <Link to="/grownups" className="small">
              Go Back?
            </Link>
            <br />
            <Link to="/subtractfunds" className="small">
              Subtract Funds?
            </Link>
          </div> */}
        </form>
      </PopupWrapper>
    </>
  );
};

export default AddFundsForm;